import { ApolloProvider } from '@apollo/client/react';
import { GlobalKeysProvider } from '@bonsai-components/react-global-keys';
import {
  Container,
  CssBaseline,
  ThemeProvider,
  useMediaQuery
} from '@mui/material';
import { LicenseInfo } from '@mui/x-license';
import { ConfirmProvider } from 'material-ui-confirm';
import type { AppProps } from 'next/app';
import { Geist, Geist_Mono } from 'next/font/google';
import { useRouter } from 'next/router';
import { SessionProvider, useSession } from 'next-auth/react';
import {
  type FunctionComponent,
  useContext,
  useEffect,
  useMemo,
  useRef
} from 'react';
import '../components/graphql-explorer/global-overrides.css';
import { TourEngineController } from '../components/guided-tour/tour-engine-controller.component';
import { Layout } from '../components/layout/layout.component';
import { ScmCallbackHandler } from '../components/scm-callback-handler/scm-callback-handler.component';
import { SiteMetaData } from '../components/site-meta-data/site-meta-data.component';
import { FlexBox } from '../components/styled-components/layouts/layouts.styled';
import { ClientSideOnly } from '../components/utilities/client-side-render/client-only.component';
import { AuthErrorBoundary } from '../components/utilities/error-boundary/auth-error-boundary.component';
import { PageErrorBoundary } from '../components/utilities/error-boundary/page-error-boundary.component';
import { UpdateConnector } from '../components/utilities/update-connector/update-connector.component';
import { WithAdministrative } from '../components/with-administrative/with-administrative.component';
import { defaultConfirmationProps } from '../constants/confirmation';

import { ApiExamplesProvider } from '../contexts/api-examples.context';
import { CommandPaletteProvider } from '../contexts/command-palette.context';
import {
  ConfigurationContext,
  ConfigurationProvider
} from '../contexts/config.context';
import { DownloadsProvider } from '../contexts/downloads.context';
import {
  NotificationContext,
  NotificationProvider
} from '../contexts/notification.context';
import { UserProvider } from '../contexts/user.context';
import { createApolloClient } from '../helpers/apollo-client.helper';
import { REFRESH_TOKEN_EXPIRED } from '../helpers/keycloak.helper';
import { signInWithError } from '../helpers/next.helpers';
import { useBuilderStore } from '../stores/builder.store';
import { useRecentRecipeStore } from '../stores/recent-recipe.store';
import { useUserPreferenceStore } from '../stores/user-preference.store';
import '../styles/global.css';
import { createModerneTheme } from '../themes/moderne-theme';

LicenseInfo.setLicenseKey(
  '83a59126797d89c582758d560a480ccbTz0xMzE1ODIsRT0xODE5NTgzOTk5MDAwLFM9cHJvLExNPWFubnVhbCxQVj1RMS0yMDI2LFE9MyxBVD1tdWx0aSxLVj0y'
);

const geist = Geist({
  subsets: ['latin'],
  display: 'auto'
});

const geistMono = Geist_Mono({
  subsets: ['latin'],
  display: 'auto'
});

/**
 * Main application component.
 * Used to enforce session management, authentication controls,
 * and other contexts.
 *
 */
function ModerneApp(props: AppProps) {
  const { Component, router } = props;
  const isAuthenticationPage = router.pathname.includes('/auth');
  const themePreference = useUserPreferenceStore(
    (state) => state.themePreference
  );
  // `system` follows the OS; useMediaQuery re-renders when the OS scheme flips.
  const systemPrefersDark = useMediaQuery('(prefers-color-scheme: dark)');
  const colorScheme =
    themePreference === 'system'
      ? systemPrefersDark
        ? 'dark'
        : 'light'
      : themePreference;
  const theme = useMemo(
    () =>
      createModerneTheme({
        mode: colorScheme,
        customFontFamily: {
          body: geist.style.fontFamily,
          monospace: geistMono.style.fontFamily
        }
      }),
    [colorScheme]
  );

  // Drive the design-system CSS-variable theme: dark token values live under
  // `[data-theme="dark"]` in @moderneinc/design-system-tokens's semantic-colors.css, so
  // flipping this attribute re-resolves every `var(--mod-*)` app-wide.
  useEffect(() => {
    document.documentElement.setAttribute('data-theme', colorScheme);
  }, [colorScheme]);

  const initializeStore = useRecentRecipeStore(
    (state) => state.initializeStore
  );

  /**
   * Initialize stores
   */
  useEffect(() => {
    initializeStore();
  }, [initializeStore]);

  if (isAuthenticationPage) {
    return (
      <ConfigurationProvider pathname={router.pathname}>
        <SiteMetaData />
        <ClientSideOnly>
          <ThemeProvider theme={theme}>
            <CssBaseline />
            <FlexBox>
              <Container disableGutters maxWidth={false}>
                <Component {...props} />
              </Container>
            </FlexBox>
          </ThemeProvider>
        </ClientSideOnly>
      </ConfigurationProvider>
    );
  } else {
    return (
      <AuthErrorBoundary>
        {/* PERF: SessionProvider fetches /api/auth/session on mount, and
            ConfigurationProvider renders children only once /api/config has
            loaded — nested the other way round, the session fetch waited. */}
        <SessionProvider
          // Refetch session every 10 minutes
          refetchInterval={60 * 10}
          // Do not fetch new sessions when tab gains focus; it caused
          // /api/auth/session to be called twice.
          refetchOnWindowFocus={false}
          // Do not refetch when offline.
          // This should avoid intermittent network issues causing the app to log out
          refetchWhenOffline={false}
        >
          <ConfigurationProvider pathname={router.pathname}>
            <ClientSideOnly>
              <SiteMetaData />
              <UserProvider>
                <ThemeProvider theme={theme}>
                  <CssBaseline />
                  <SessionExpiryRedirect />
                  <NotificationProvider>
                    <AuthorizedModerneApp {...props} />
                  </NotificationProvider>
                </ThemeProvider>
              </UserProvider>
            </ClientSideOnly>
          </ConfigurationProvider>
        </SessionProvider>
      </AuthErrorBoundary>
    );
  }
}

// Stateless on purpose: a tab-visibility flag here re-rendered the whole app.
// @see https://next-auth.js.org/v3/tutorials/refresh-token-rotation#client-side
const SessionExpiryRedirect: FunctionComponent = () => {
  const router = useRouter();
  const { data: session, status } = useSession();
  const sessionInvalid =
    !session?.accessToken || session?.error === REFRESH_TOKEN_EXPIRED;

  useEffect(() => {
    if (status === 'loading' || !router.isReady || !sessionInvalid) {
      return;
    }
    signInWithError('sessionExpired', router.asPath, 'sessionExpired');
  }, [status, sessionInvalid, router.isReady, router.asPath]);

  return null;
};

/**
 * Components in this component are either responsible for handling
 * authentication or authorization based on the session or depend upon a
 * successfully authenticated / authorized user to render.
 */
const AuthorizedModerneApp: FunctionComponent<AppProps> = ({
  Component,
  pageProps
}) => {
  const router = useRouter();
  // Use selective subscriptions to prevent unnecessary re-renders
  const initialize = useBuilderStore((state) => state.initialize);
  const { data: session } = useSession();
  const { getApiGatewayUrl } = useContext(ConfigurationContext);
  const { renderNotification } = useContext(NotificationContext);

  const disableKeyboardShortcuts = useUserPreferenceStore(
    (state) => state.disableKeyboardShortcuts
  );
  // PERF: the auth link reads this ref per request, so a token refresh no longer
  // rebuilds the client (which dropped the cache and refetched every query).
  const accessTokenRef = useRef(session?.accessToken);
  accessTokenRef.current = session?.accessToken;
  const apiGatewayUrl = getApiGatewayUrl();
  const client = useMemo(
    () =>
      createApolloClient({
        getAccessToken: () => accessTokenRef.current,
        apiGatewayUrl,
        showError: renderNotification
      }),
    [apiGatewayUrl, renderNotification]
  );

  // biome-ignore lint/correctness/useExhaustiveDependencies: run once on mount
  useEffect(() => {
    initialize(router);
  }, []);

  /**
   * Conditionally render the application based on the session.
   * If there's no session with an access token, we will be unable to issue
   * any GraphQL requests.
   */
  return session?.accessToken ? (
    <ApolloProvider client={client}>
      <ScmCallbackHandler />
      <ApiExamplesProvider>
        {/* The tour controller is a side-effect-only component reading the
            tour-engine Zustand store. It sits ABOVE the route-keyed error
            boundary so client-side navigation doesn't tear it down (which would
            blink the Joyride overlay and re-run the target-wait every nav). */}
        <TourEngineController />
        <PageErrorBoundary key={Component.name}>
          <ConfirmProvider defaultOptions={defaultConfirmationProps}>
            <DownloadsProvider>
              <GlobalKeysProvider
                useCtrlAsMetaAlternative
                disableKeybindings={disableKeyboardShortcuts}
              >
                <CommandPaletteProvider>
                  <Layout>
                    <PageErrorBoundary key={Component.name}>
                      <Component {...pageProps} id="moderne-page-content" />
                    </PageErrorBoundary>
                  </Layout>
                  <WithAdministrative suppressWarning>
                    <UpdateConnector />
                  </WithAdministrative>
                </CommandPaletteProvider>
              </GlobalKeysProvider>
            </DownloadsProvider>
          </ConfirmProvider>
        </PageErrorBoundary>
      </ApiExamplesProvider>
    </ApolloProvider>
  ) : null;
};

export default ModerneApp;
